import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
} from "react";
import { AdminLoginCredentials, AuthAction, AuthState } from "../types/auth";

const baseURL = import.meta.env.VITE_API_URL;

const initialState: AuthState = {
  isAuthenticated: false,
  token: null,
  user: null,
  loading: true,
  error: null,
  requiresTwoFactor: false,
  tempUserId: undefined,
};

interface AuthContextType {
  state: AuthState;
  login: (credentials: AdminLoginCredentials) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  validate2FA: (token: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

function authReducer(state: AuthState, action: AuthAction): AuthState {
  console.log("Stare curentă:", state);
  console.log("Acțiune:", action);

  switch (action.type) {
    case "REQUIRE_2FA":
      return {
        ...state,
        requiresTwoFactor: true,
        tempUserId: action.payload.user.id,
        user: action.payload.user,
        loading: false,
        error: null,
      };
    case "VALIDATE_2FA":
      return {
        ...state,
        isAuthenticated: true,
        token: action.payload.token,
        user: action.payload.user,
        requiresTwoFactor: false,
        tempUserId: undefined,
        loading: false,
        error: null,
      };
    case "LOGIN_REQUEST":
      return {
        ...state,
        loading: true,
        error: null,
      };
    case "LOGIN_SUCCESS":
      return {
        ...state,
        isAuthenticated: true,
        token: action.payload.token,
        user: action.payload.user,
        loading: false,
        error: null,
      };
    case "LOGIN_FAILURE":
      return {
        ...state,
        isAuthenticated: false,
        token: null,
        user: null,
        loading: false,
        error: action.payload,
      };
    case "INIT_COMPLETE":
      return {
        ...state,
        loading: false,
      };
    case "LOGOUT":
      return {
        ...initialState,
        loading: false,
      };
    case "CLEAR_ERROR":
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({
  children,
}: AuthProviderProps): React.ReactElement {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Verifică token-ul existent la pornire
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      // Verifică token-ul și obține datele utilizatorului
      fetchWithAuth(`/api/admin/auth/check`)
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error("Token invalid");
        })
        .then((data) => {
          dispatch({
            type: "LOGIN_SUCCESS",
            payload: { token, user: data.user },
          });
        })
        .catch(() => {
          localStorage.removeItem("adminToken");
        })
        .finally(() => {
          dispatch({ type: "INIT_COMPLETE" });
        });
    } else {
      dispatch({ type: "INIT_COMPLETE" });
    }
  }, []);

  useEffect(() => {
    console.log("Stare autentificare actualizată:", state);
  }, [state]);

  const login = async (credentials: AdminLoginCredentials) => {
    try {
      dispatch({ type: "LOGIN_REQUEST" });
      const response = await fetch(`${baseURL}/api/admin/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Autentificare eșuată");
      }

      const data = await response.json();
      console.log("Răspuns autentificare:", data);

      if (data.requiresTwoFactor) {
        dispatch({ type: "REQUIRE_2FA", payload: { user: data.user } });
      } else {
        dispatch({ type: "LOGIN_SUCCESS", payload: data });
        localStorage.setItem("adminToken", data.token);
      }
    } catch (error) {
      dispatch({
        type: "LOGIN_FAILURE",
        payload:
          error instanceof Error ? error.message : "Autentificare eșuată",
      });
    }
  };

  const logout = () => {
    localStorage.removeItem("adminToken");
    dispatch({ type: "LOGOUT" });
  };

  const clearError = () => {
    dispatch({ type: "CLEAR_ERROR" });
  };

  return React.createElement(AuthContext.Provider, {
    value: {
      state,
      login,
      logout,
      clearError,
      validate2FA: async (token: string) => {
        try {
          dispatch({ type: "LOGIN_REQUEST" });
          const response = await fetch(`${baseURL}/api/admin/auth/verify-2fa`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              userId: state.tempUserId,
              token,
            }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || "Cod 2FA invalid");
          }

          const data = await response.json();
          dispatch({ type: "VALIDATE_2FA", payload: data });
          localStorage.setItem("adminToken", data.token);
        } catch (error) {
          dispatch({
            type: "LOGIN_FAILURE",
            payload:
              error instanceof Error ? error.message : "Validare 2FA eșuată",
          });
        }
      },
    },
    children,
  });
}

// Funcție ajutătoare pentru cereri autentificate
export async function fetchWithAuth(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = localStorage.getItem("adminToken");
  const headers = {
    ...options.headers,
    Authorization: `Bearer ${token}`,
  };
  return fetch(`${baseURL}${url}`, { ...options, headers });
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth trebuie folosit în interiorul unui AuthProvider");
  }
  return context;
}
